import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MOVE_OUT_BCC_MAX,
  MOVE_OUT_CC_MAX,
  MOVE_OUT_COVER_NOTE_MAX,
  type MoveOutDeliveryDTO,
  type MoveOutDeliveryRecipientDTO,
  type MoveOutDeliveryStateDTO,
  type MoveOutReportDTO,
} from '@propmgt/shared';
import { api, errorMessage } from '../../api/client';
import { formatDateTime } from '../../lib/date';
import { Loading, StatusMessage } from '../ui/Feedback';

/**
 * 0.27.0 — "Sign & send" on a FINALIZED move-out report (owner only; the API refuses staff):
 *   1. Sign — appends a signature page to a COPY of the statement (the statement is untouched).
 *   2. Delivery emails — one per recipient (the only thing still editable after finalize).
 *   3. Email — one message per recipient with the signed statement, the receipt / invoice copies
 *      when they fit, and a private 90-day download link; replies go to the reply-to address.
 * Staff see the signed status and can download the signed copy, nothing else.
 */

export function deliveryKey(reportId: string) {
  return ['admin', 'move-out-delivery', reportId] as const;
}

const MODE_TEXT: Record<MoveOutDeliveryDTO['attachmentMode'], string> = {
  full: 'signed statement + receipts attached',
  statement_only: 'signed statement attached; receipts by download link',
  link_only: 'download link only (too large to attach)',
};

function mb(bytes: number): string {
  return bytes >= 1_048_576 ? `${(bytes / 1_048_576).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/**
 * 0.28.3 — read the "Blind copy" box: addresses separated by commas, semicolons, spaces or new
 * lines; lower-cased and de-duplicated. `invalid` lists the entries that are not an email address,
 * `tooMany` is set past `MOVE_OUT_BCC_MAX`. (A first check only — the server validates again.)
 */
export function parseBcc(raw: string, max: number = MOVE_OUT_BCC_MAX): { list: string[]; invalid: string[]; tooMany: boolean } {
  const list = [...new Set(raw.split(/[\s,;]+/).map((s) => s.trim().toLowerCase()).filter((s) => s !== ''))];
  const invalid = list.filter((a) => a.length > 254 || !/^[^\s@<>()[\]\\,;:"]+@[^\s@<>()[\]\\,;:"]+\.[^\s@<>()[\]\\,;:".]{2,}$/.test(a));
  return { list, invalid, tooMany: list.length > max };
}

/** 0.28.4 — the "Copy (cc)" box: read exactly like the blind-copy box, capped at `MOVE_OUT_CC_MAX`. */
export function parseCc(raw: string): { list: string[]; invalid: string[]; tooMany: boolean } {
  return parseBcc(raw, MOVE_OUT_CC_MAX);
}

/* ------------------------------ delivery email row ------------------------------ */

/** Is the typed value different from the saved delivery email? */
function emailDirty(draft: string | undefined, saved: string | null): boolean {
  return draft !== undefined && draft.trim().toLowerCase() !== (saved ?? '');
}

function RecipientEmailRow({
  reportId,
  recipient,
  draft,
  onDraft,
  onState,
}: {
  reportId: string;
  recipient: MoveOutDeliveryRecipientDTO;
  /** The unsaved edit (undefined = untouched). Held by the parent so Send can refuse while it is unsaved. */
  draft: string | undefined;
  onDraft: (value: string | undefined) => void;
  onState: (s: MoveOutDeliveryStateDTO) => void;
}) {
  const email = draft ?? recipient.email ?? '';
  const setEmail = onDraft;
  const save = useMutation({
    mutationFn: () => api.moveOut.setRecipientDeliveryEmail(reportId, recipient.id, { email: email.trim() === '' ? null : email.trim() }),
    onSuccess: (st) => {
      onState(st);
      onDraft(undefined);
    },
  });
  const dirty = emailDirty(draft, recipient.email);
  const id = `mo-email-${recipient.id}`;
  return (
    <form
      className="stack"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <div>
        <label htmlFor={id}>Delivery email — {recipient.tenantName}</label>
        <div className="row">
          <input
            id={id}
            type="email"
            value={email}
            maxLength={254}
            autoComplete="off"
            style={{ flex: 1, minWidth: '14rem' }}
            onChange={(e) => setEmail(e.target.value)}
          />
          <button type="submit" className="secondary" disabled={save.isPending || !dirty}>
            {save.isPending ? 'Saving…' : 'Save'}
            <span className="visually-hidden"> email for {recipient.tenantName}</span>
          </button>
        </div>
        {!recipient.email && !dirty && <p className="field-hint">No delivery email on file — required before sending to {recipient.tenantName}.</p>}
        {dirty && (
          <p className="field-hint">
            Not saved yet.{' '}
            {recipient.email && recipient.emailedCurrentCopy !== 'no'
              ? 'Saving a different address also switches off the download link already emailed to the old one.'
              : ''}
          </p>
        )}
      </div>
      {save.isError && <StatusMessage tone="err">{errorMessage(save.error)}</StatusMessage>}
      {save.isSuccess && !dirty && <StatusMessage tone="ok">Saved.</StatusMessage>}
    </form>
  );
}

/* ---------------------------------- the panel ---------------------------------- */

export function SignAndSend({ report, isOwner, ownerEmail }: { report: MoveOutReportDTO; isOwner: boolean; ownerEmail?: string }) {
  const queryClient = useQueryClient();
  const download = useMutation({
    mutationFn: (docId: string) => api.documents.signedUrl(docId),
    onSuccess: (resp) => window.open(resp.url, '_blank', 'noopener'),
  });

  if (!isOwner) {
    return (
      <section aria-labelledby="mo-signsend" className="stack">
        <h2 id="mo-signsend">Sign &amp; send</h2>
        {download.isError && <StatusMessage tone="err">{errorMessage(download.error)}</StatusMessage>}
        {report.signedDocumentId ? (
          <div className="row">
            <span className="badge badge--ok">Signed {formatDateTime(report.signedAt)}</span>
            <button type="button" className="secondary" disabled={download.isPending} onClick={() => download.mutate(report.signedDocumentId!)}>
              Download signed statement
            </button>
          </div>
        ) : (
          <p className="muted">Not signed yet.</p>
        )}
        <p className="muted">Only the owner can sign the statement and email it to the recipients.</p>
      </section>
    );
  }
  return (
    <OwnerSignAndSend
      report={report}
      ownerEmail={ownerEmail ?? ''}
      onSigned={() => void queryClient.invalidateQueries({ queryKey: ['admin', 'move-out', report.leaseId] })}
    />
  );
}

function OwnerSignAndSend({ report, ownerEmail, onSigned }: { report: MoveOutReportDTO; ownerEmail: string; onSigned: () => void }) {
  const queryClient = useQueryClient();
  const stateQuery = useQuery({ queryKey: deliveryKey(report.id), queryFn: () => api.moveOut.delivery(report.id) });
  const setState = (s: MoveOutDeliveryStateDTO) => queryClient.setQueryData(deliveryKey(report.id), s);

  const [subject, setSubject] = useState<string | null>(null);
  const [coverNote, setCoverNote] = useState<string | null>(null);
  const [ack, setAck] = useState(false);
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const [lastSend, setLastSend] = useState<{ sent: number; failed: number; warnings: string[] } | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string | undefined>>({});
  const [testTarget, setTestTarget] = useState<'self' | 'reply_to'>('self');
  const [bccRaw, setBccRaw] = useState('');
  const [ccRaw, setCcRaw] = useState('');

  const download = useMutation({
    mutationFn: (docId: string) => api.documents.signedUrl(docId),
    onSuccess: (resp) => window.open(resp.url, '_blank', 'noopener'),
  });
  const sign = useMutation({
    mutationFn: (force: boolean) => api.moveOut.sign(report.id, { force }),
    onSuccess: (resp) => {
      setState(resp.state);
      onSigned();
      void queryClient.invalidateQueries({ queryKey: ['admin', 'documents'] });
    },
  });
  const send = useMutation({
    mutationFn: (v: { recipientIds: string[]; resend: boolean; subject: string; coverNote: string; cc: string[]; bcc: string[] }) =>
      api.moveOut.send(report.id, {
        recipientIds: v.recipientIds,
        subject: v.subject,
        coverNote: v.coverNote,
        acknowledgeEmailDelivery: true,
        resend: v.resend,
        ...(v.cc.length > 0 ? { cc: v.cc } : {}),
        ...(v.bcc.length > 0 ? { bcc: v.bcc } : {}),
      }),
    onSuccess: (resp) => {
      setState(resp.state);
      setLastSend({ sent: resp.sent, failed: resp.failed, warnings: resp.warnings });
    },
    onError: () => void stateQuery.refetch(),
  });
  // A TEST copy to the owner's own address: the real email, but nothing is recorded as a delivery.
  const test = useMutation({
    mutationFn: (v: { subject: string; coverNote: string; cc: string[]; bcc: string[] }) =>
      api.moveOut.send(report.id, {
        testTo: testTarget,
        subject: v.subject,
        coverNote: v.coverNote,
        ...(v.cc.length > 0 ? { cc: v.cc } : {}),
        ...(v.bcc.length > 0 ? { bcc: v.bcc } : {}),
      }),
  });

  if (stateQuery.isLoading) return <Loading label="Loading signing and delivery status…" />;
  if (stateQuery.isError || !stateQuery.data) {
    return (
      <section aria-labelledby="mo-signsend" className="stack">
        <h2 id="mo-signsend">Sign &amp; send</h2>
        <StatusMessage tone="err">{errorMessage(stateQuery.error)}</StatusMessage>
      </section>
    );
  }
  const state = stateQuery.data;
  const sig = state.signature;
  const profile = state.signatureProfile;
  const subjectValue = subject ?? state.email.defaultSubject;
  const noteValue = coverNote ?? state.email.defaultCoverNote;

  // Anything but `no` means this signed copy already went (or may have gone) to them → a RESEND.
  const emailedIds = new Set(state.recipients.filter((r) => r.emailedCurrentCopy !== 'no').map((r) => r.id));
  const selected = state.recipients.filter((r) => !excluded.has(r.id));
  const missingEmail = selected.filter((r) => !r.email);
  // An address typed but not saved would NOT be the one emailed — refuse to send until it is saved.
  const unsaved = state.recipients.filter((r) => emailDirty(drafts[r.id], r.email));
  // Copies: nothing is sent (not even a test copy) while either box holds a bad or a sixth address.
  const cc = parseCc(ccRaw);
  const ccBad = cc.invalid.length > 0 || cc.tooMany;
  const bccTyped = parseBcc(bccRaw);
  // An address typed in BOTH boxes is sent as a visible copy only (the server applies the same rule).
  const inBoth = bccTyped.list.filter((a) => cc.list.includes(a));
  const bcc = { ...bccTyped, list: bccTyped.list.filter((a) => !cc.list.includes(a)) };
  const copiesBad = bccTyped.invalid.length > 0 || bccTyped.tooMany || ccBad;
  const sendBlocked =
    !sig || !ack || !state.email.providerReady || send.isPending || unsaved.length > 0 || subjectValue.trim() === '' || copiesBad;
  const canSend = !sendBlocked && selected.length > 0 && missingEmail.length === 0;

  const doSend = (recipientIds: string[]) => {
    const targets = state.recipients.filter((r) => recipientIds.includes(r.id));
    // The confirmation names every address, so a wrong one is caught before it is emailed.
    const lines = targets.map((r) => `  ${r.tenantName} <${r.email ?? 'no email'}>${emailedIds.has(r.id) ? '  (already emailed - this is a RESEND)' : ''}`);
    const again = targets.filter((r) => emailedIds.has(r.id));
    // …and every copy address: each of them receives a copy of EVERY email listed above.
    const ccLines =
      cc.list.length > 0
        ? `\n\nCopy (cc) on each of those emails - ${cc.list.length} address${cc.list.length === 1 ? '' : 'es'}, VISIBLE to the recipient:\n${cc.list.map((a) => `  ${a}`).join('\n')}\nA copied address sees the whole email, including that recipient's private download link.`
        : '';
    const bccLines =
      bcc.list.length > 0
        ? `\n\nBlind copy (bcc) on each of those emails - ${bcc.list.length} address${bcc.list.length === 1 ? '' : 'es'}:\n${bcc.list.map((a) => `  ${a}`).join('\n')}\nA blind-copied address sees the whole email, including that recipient's private download link.`
        : '';
    const question = `Email the signed statement to:\n${lines.join('\n')}${ccLines}${bccLines}\n\nEach gets their own email.${again.length > 0 ? ' Recipients marked RESEND get it again.' : ''} Send now?`;
    if (!window.confirm(question)) return;
    setLastSend(null);
    send.mutate({ recipientIds, resend: again.length > 0, subject: subjectValue.trim(), coverNote: noteValue, cc: cc.list, bcc: bcc.list });
  };

  return (
    <section aria-labelledby="mo-signsend" className="stack">
      <h2 id="mo-signsend">Sign &amp; send</h2>

      {/* ---- 1. sign ---- */}
      <div className="card stack">
        <h3 style={{ marginTop: 0 }}>1. Sign the statement</h3>
        {sign.isError && <StatusMessage tone="err">{errorMessage(sign.error)}</StatusMessage>}
        {download.isError && <StatusMessage tone="err">{errorMessage(download.error)}</StatusMessage>}
        {sig ? (
          <>
            <p>
              <span className="badge badge--ok">Signed</span> by <strong>{sig.signerName}</strong>, {sig.signerTitle} on{' '}
              {formatDateTime(sig.signedAt)}
              {sig.hasHandwrittenImage ? ' — typed and handwritten signature.' : ' — typed signature only (no handwritten image was on file).'}
            </p>
            <p className="muted">
              The signature page was added to a copy; the finalized statement itself is unchanged.
              {state.signatureHistory.length > 1 ? ` ${state.signatureHistory.length - 1} earlier signed ${state.signatureHistory.length === 2 ? 'copy is' : 'copies are'} kept as superseded.` : ''}
            </p>
            <div className="row">
              <button type="button" disabled={download.isPending} onClick={() => download.mutate(sig.documentId)}>
                Download signed statement
              </button>
              <button
                type="button"
                className="secondary"
                disabled={sign.isPending || !profile.ready}
                onClick={() => {
                  if (window.confirm('Sign again? This makes a NEW signed copy with the current date and marks the existing one superseded (both are kept).')) sign.mutate(true);
                }}
              >
                {sign.isPending ? 'Signing…' : 'Sign again (new copy)'}
              </button>
            </div>
          </>
        ) : (
          <>
            {!profile.ready ? (
              <StatusMessage tone="warn">
                Set your signature name and title first — <Link to="/admin/account">Account → Signature</Link>.
              </StatusMessage>
            ) : (
              <>
                <p>
                  You will sign as <strong>{profile.signerName}</strong>, {profile.signerTitle}.
                </p>
                {!profile.image && (
                  <StatusMessage tone="warn">
                    No handwritten signature image is on file, so the statement would carry your typed signature only. Add one at{' '}
                    <Link to="/admin/account">Account → Signature</Link> before signing if you want both.
                  </StatusMessage>
                )}
              </>
            )}
            <p className="muted">
              Signing appends a signature page (your name and title, {profile.image ? 'your handwritten signature, ' : ''}the date and time, and an
              electronic-signature record) to a copy of the statement. The finalized statement is not changed.
            </p>
            <div>
              <button
                type="button"
                disabled={sign.isPending || !profile.ready}
                onClick={() => {
                  if (window.confirm(`Sign the finalized statement as ${profile.signerName}, ${profile.signerTitle}?`)) sign.mutate(false);
                }}
              >
                {sign.isPending ? 'Signing…' : 'Sign statement'}
              </button>
            </div>
          </>
        )}
      </div>

      {/* ---- 2. delivery emails ---- */}
      <div className="card stack">
        <h3 style={{ marginTop: 0 }}>2. Recipient delivery emails</h3>
        <p className="muted">The email address is the only recipient detail that can still be changed after finalizing.</p>
        {state.recipients.length === 0 && <p className="muted">This report has no recipients.</p>}
        {state.recipients.map((r) => (
          <RecipientEmailRow
            key={r.id}
            reportId={report.id}
            recipient={r}
            draft={drafts[r.id]}
            onDraft={(v) => setDrafts((d) => ({ ...d, [r.id]: v }))}
            onState={setState}
          />
        ))}
        <p className="field-hint">
          Changing or clearing a saved address switches off every download link already emailed to that recipient (use it if a statement went to a wrong
          address), so send again afterwards.
        </p>
      </div>

      {/* ---- 3. send ---- */}
      <div className="card stack">
        <h3 style={{ marginTop: 0 }}>3. Email the signed statement</h3>
        {!sig && <p className="muted">Sign the statement first.</p>}
        {!state.email.providerReady && (
          <StatusMessage tone="err">Email sending is not configured on this server, so nothing can be sent from here.</StatusMessage>
        )}
        <dl className="kv-grid">
          <div>
            <dt className="muted">From</dt>
            <dd>{state.email.from}</dd>
          </div>
          <div>
            <dt className="muted">Replies go to</dt>
            <dd>{state.email.replyTo}</dd>
          </div>
        </dl>
        <fieldset className="stack" style={{ border: 0, padding: 0, margin: 0 }}>
          <legend>Send to (one separate email each)</legend>
          {state.recipients.map((r) => (
            <label key={r.id} htmlFor={`mo-send-${r.id}`} style={{ display: 'block', fontWeight: 'normal' }}>
              <input
                id={`mo-send-${r.id}`}
                type="checkbox"
                checked={!excluded.has(r.id)}
                onChange={(e) => {
                  const next = new Set(excluded);
                  if (e.target.checked) next.delete(r.id);
                  else next.add(r.id);
                  setExcluded(next);
                }}
              />{' '}
              {r.tenantName} — {r.email ?? 'no delivery email yet'}
              {r.emailedCurrentCopy === 'sent'
                ? ' (already emailed this signed copy)'
                : r.emailedCurrentCopy === 'unknown'
                  ? ' (an earlier send was interrupted — it may have been delivered)'
                  : r.emailedCurrentCopy === 'sending'
                    ? ' (a send is in progress)'
                    : ''}
            </label>
          ))}
        </fieldset>
        <div>
          <label htmlFor="mo-cc">Copy (cc, optional)</label>
          <textarea
            id="mo-cc"
            rows={2}
            value={ccRaw}
            autoComplete="off"
            spellCheck={false}
            aria-describedby="mo-cc-hint"
            aria-invalid={ccBad}
            onChange={(e) => setCcRaw(e.target.value)}
          />
          <p id="mo-cc-hint" className="field-hint">
            Up to {MOVE_OUT_CC_MAX} email addresses, separated by commas or new lines. Each one is copied on every recipient&rsquo;s email (and on a test
            copy) — the recipients <strong>do see</strong> these addresses. A copied address gets the whole email, including that recipient&rsquo;s
            private download link.
          </p>
          {cc.invalid.length > 0 && <StatusMessage tone="err">Not a valid email address (cc): {cc.invalid.join(', ')}</StatusMessage>}
          {cc.tooMany && (
            <StatusMessage tone="err">
              At most {MOVE_OUT_CC_MAX} copy (cc) addresses — {cc.list.length} are listed.
            </StatusMessage>
          )}
        </div>
        <div>
          <label htmlFor="mo-bcc">Blind copy (optional)</label>
          <textarea
            id="mo-bcc"
            rows={2}
            value={bccRaw}
            autoComplete="off"
            spellCheck={false}
            aria-describedby="mo-bcc-hint"
            aria-invalid={bccTyped.invalid.length > 0 || bccTyped.tooMany}
            onChange={(e) => setBccRaw(e.target.value)}
          />
          <p id="mo-bcc-hint" className="field-hint">
            Up to {MOVE_OUT_BCC_MAX} email addresses, separated by commas or new lines. Each one is blind-copied on every recipient&rsquo;s email (and on a
            test copy) — the recipients do not see them. A blind-copied address gets the whole email, including that recipient&rsquo;s private download
            link.
          </p>
          {bccTyped.invalid.length > 0 && <StatusMessage tone="err">Not a valid email address: {bccTyped.invalid.join(', ')}</StatusMessage>}
          {bccTyped.tooMany && (
            <StatusMessage tone="err">
              At most {MOVE_OUT_BCC_MAX} blind-copy addresses — {bccTyped.list.length} are listed.
            </StatusMessage>
          )}
          {inBoth.length > 0 && (
            <StatusMessage tone="warn">Listed as both a copy and a blind copy — sent as a visible copy (cc) only: {inBoth.join(', ')}</StatusMessage>
          )}
        </div>
        <div>
          <label htmlFor="mo-subject">Subject</label>
          <input id="mo-subject" value={subjectValue} maxLength={200} onChange={(e) => setSubject(e.target.value)} />
        </div>
        <div>
          <label htmlFor="mo-cover">Cover note (appears above the summary; plain text)</label>
          <textarea id="mo-cover" rows={7} maxLength={MOVE_OUT_COVER_NOTE_MAX} value={noteValue} onChange={(e) => setCoverNote(e.target.value)} />
          <p className="field-hint">
            {noteValue.length.toLocaleString()} / {MOVE_OUT_COVER_NOTE_MAX.toLocaleString()} characters. The email also shows the deposit held, total
            deductions, refund due and balance owed, lists the attachments, and says replies go to {state.email.replyTo}.
          </p>
        </div>
        {state.packet && (
          <div>
            <p style={{ marginBottom: '0.25rem' }}>
              <strong>What each recipient gets:</strong>
            </p>
            <ul>
              {state.packet.items.map((i) => {
                const attached = state.packet!.mode === 'full' ? i.emailable : state.packet!.mode === 'statement_only' ? i.kind === 'signed_statement' : false;
                return (
                  <li key={i.documentId}>
                    {i.filename} ({i.kind === 'signed_statement' ? 'signed statement' : i.kind}, {mb(i.sizeBytes)}) —{' '}
                    {!i.available ? <strong>MISSING from storage — will NOT be included</strong> : attached ? 'attached' : 'by download link'}
                  </li>
                );
              })}
              <li>A private download link to all of the above, valid 90 days.</li>
            </ul>
            {state.packet.excluded.length > 0 && (
              <StatusMessage tone="warn">
                Not included:{' '}
                {state.packet.excluded
                  .map((x) => `${x.filename} (${x.kind}) — belongs to another ${x.reason === 'other_lease' ? 'lease' : 'tenant'}`)
                  .join('; ')}
                . A document filed under a different lease or tenant is never emailed or put behind the link.
              </StatusMessage>
            )}
            {state.packet.items.some((i) => !i.available) && (
              <StatusMessage tone="err">
                {state.packet.items.some((i) => !i.available && i.kind === 'signed_statement')
                  ? 'The signed statement file is missing from storage — nothing can be sent. Sign again (new copy).'
                  : 'A receipt / invoice file is missing from storage. It will be left out of the email and the download link — send that copy another way.'}
              </StatusMessage>
            )}
            {state.packet.mode !== 'full' && (
              <StatusMessage tone="warn">
                {state.packet.mode === 'statement_only'
                  ? `The receipts and invoices are too large to attach (limit ${mb(state.email.maxAttachmentBytes)} per email), so each email carries the signed statement and the download link for the receipts.`
                  : `The signed statement is too large to attach (limit ${mb(state.email.maxAttachmentBytes)} per email), so each email carries only the download link.`}{' '}
                § 1950.5(g)(2) calls for copies of the bills to be included with the statement — consider also mailing them.
              </StatusMessage>
            )}
          </div>
        )}
        <div className="status status--warn">
          <p style={{ marginTop: 0 }}>
            <strong>Delivery method.</strong> Civil Code § 1950.5(g)(1) calls for the statement to be delivered personally or by first-class mail,
            postage prepaid, unless the landlord and tenant have agreed to delivery by email. Emailing it from here does not replace mailing it unless
            that agreement exists.
          </p>
          <label htmlFor="mo-ack" style={{ display: 'block', fontWeight: 'normal' }}>
            <input id="mo-ack" type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} /> I understand, and I am choosing to send this
            statement by email.
          </label>
        </div>
        {missingEmail.length > 0 && sig && (
          <p className="muted">Add a delivery email for {missingEmail.map((r) => r.tenantName).join(', ')} (or untick them) to send.</p>
        )}
        {unsaved.length > 0 && (
          <StatusMessage tone="warn">
            The delivery email typed for {unsaved.map((r) => r.tenantName).join(', ')} is not saved — press Save on that row (or restore the old address)
            before sending.
          </StatusMessage>
        )}
        {lastSend &&
          lastSend.warnings.map((w) => (
            <StatusMessage key={w} tone="warn">
              {w}
            </StatusMessage>
          ))}
        {send.isError && <StatusMessage tone="err">{errorMessage(send.error)}</StatusMessage>}
        {lastSend && lastSend.failed === 0 && (
          <StatusMessage tone="ok">
            Sent {lastSend.sent} email{lastSend.sent === 1 ? '' : 's'}.
          </StatusMessage>
        )}
        {lastSend && lastSend.failed > 0 && (
          <StatusMessage tone="err">
            {lastSend.failed} email{lastSend.failed === 1 ? '' : 's'} failed{lastSend.sent > 0 ? ` (${lastSend.sent} sent)` : ''} — see the delivery log below.
          </StatusMessage>
        )}
        <div>
          <button type="button" disabled={!canSend} onClick={() => doSend(selected.map((r) => r.id))}>
            {send.isPending ? 'Sending…' : `Email signed statement to ${selected.length} recipient${selected.length === 1 ? '' : 's'}`}
          </button>
        </div>

        <div className="stack" style={{ borderTop: '1px solid var(--border)', paddingTop: '0.75rem' }}>
          <label htmlFor="mo-test-to">Send a test copy to yourself first</label>
          <div className="row">
            <select id="mo-test-to" value={testTarget} onChange={(e) => setTestTarget(e.target.value === 'reply_to' ? 'reply_to' : 'self')}>
              <option value="self">My sign-in address{ownerEmail ? ` (${ownerEmail})` : ''}</option>
              <option value="reply_to">The reply-to mailbox ({state.email.replyTo})</option>
            </select>
            <button
              type="button"
              className="secondary"
              disabled={!sig || test.isPending || !state.email.providerReady || copiesBad}
              onClick={() => {
                // With copies the test leaves your own mailbox — name every address first.
                const named = [
                  ...(cc.list.length > 0 ? [`copy (cc) it to:\n${cc.list.map((a) => `  ${a}`).join('\n')}`] : []),
                  ...(bcc.list.length > 0 ? [`blind-copy (bcc) it to:\n${bcc.list.map((a) => `  ${a}`).join('\n')}`] : []),
                ];
                if (named.length > 0 && !window.confirm(`Send the TEST copy to your own mailbox and ${named.join('\nand ')}\n\nSend the test now?`)) {
                  return;
                }
                test.mutate({ subject: subjectValue.trim() || state.email.defaultSubject, coverNote: noteValue, cc: cc.list, bcc: bcc.list });
              }}
            >
              {test.isPending ? 'Sending test…' : 'Send test copy'}
            </button>
          </div>
          <p className="field-hint">
            The exact email the recipients would get (same sender and attachments), marked TEST, sent only to your own mailbox
            {cc.list.length > 0 ? ` and copied (cc) to the ${cc.list.length} address${cc.list.length === 1 ? '' : 'es'} above` : ''}
            {bcc.list.length > 0 ? ` and blind-copied to the ${bcc.list.length} address${bcc.list.length === 1 ? '' : 'es'} above` : ''}. Nothing is added
            to the delivery log, and opening its download link is not recorded as a tenant opening it.
          </p>
          {test.isError && <StatusMessage tone="err">{errorMessage(test.error)}</StatusMessage>}
          {test.data?.test?.status === 'sent' && (
            <StatusMessage tone="ok">
              Test copy sent to {test.data.test.to} from {test.data.test.from} — {MODE_TEXT[test.data.test.attachmentMode]} (
              {test.data.test.attachmentCount} attachment{test.data.test.attachmentCount === 1 ? '' : 's'}
              {test.data.test.ccCount > 0 ? `; cc: ${test.data.test.ccCount}` : ''}
              {test.data.test.bccCount > 0 ? `; bcc: ${test.data.test.bccCount}` : ''}). Check that it arrived, that the attachments open, and that Reply
              goes to {state.email.replyTo}.
            </StatusMessage>
          )}
          {test.data?.test?.status === 'failed' && (
            <StatusMessage tone="err">The test copy could not be sent: {test.data.test.error ?? 'the email provider refused it.'}</StatusMessage>
          )}
        </div>
      </div>

      {/* ---- delivery register ---- */}
      <div className="stack">
        <h3 id="mo-delivery-log">Delivery log</h3>
        {state.deliveries.length === 0 ? (
          <p className="muted">Nothing has been emailed yet.</p>
        ) : (
          <ul aria-labelledby="mo-delivery-log" className="stack" style={{ listStyle: 'none', padding: 0 }}>
            {state.deliveries.map((d) => (
              <li key={d.id} className="card">
                <p style={{ margin: 0 }}>
                  {d.status === 'sent' ? 'Sent to' : d.status === 'failed' ? 'Could not send to' : 'Sending to'} <strong>{d.toMasked}</strong> ({d.tenantName}) on{' '}
                  {formatDateTime(d.sentAt ?? d.createdAt)} —{' '}
                  <span className={`badge ${d.status === 'sent' ? 'badge--ok' : d.status === 'failed' ? 'badge--err' : 'badge--warn'}`}>
                    {d.status === 'sent' ? 'Sent' : d.status === 'failed' ? 'Failed' : 'In progress — not confirmed'}
                  </span>
                </p>
                <p className="muted" style={{ marginBottom: 0 }}>
                  {d.status === 'failed'
                    ? (d.error ?? 'The email provider refused the message.')
                    : d.status === 'sending'
                      ? 'The request did not finish — the email may or may not have gone out.'
                      : MODE_TEXT[d.attachmentMode]}
                  {d.status === 'sent' && ` · ${d.attachments.length} attachment${d.attachments.length === 1 ? '' : 's'}`} · from {d.from}
                  {d.ccCount > 0 ? ` · cc: ${d.ccCount}${d.ccMasked.length > 0 ? ` (${d.ccMasked.join(', ')})` : ''}` : ''}
                  {d.bccCount > 0 ? ` · bcc: ${d.bccCount}${d.bccMasked.length > 0 ? ` (${d.bccMasked.join(', ')})` : ''}` : ''}
                  {d.signedDocumentId !== sig?.documentId ? ' · an earlier (superseded) signed copy' : ''}
                </p>
                <div className="row" style={{ marginTop: '0.5rem' }}>
                  <button type="button" className="secondary" disabled={sendBlocked} onClick={() => doSend([d.recipientId])}>
                    {d.status === 'sent' ? 'Resend' : 'Try again'}
                    <span className="visually-hidden"> to {d.tenantName}</span>
                  </button>
                  {!ack && <span className="muted">Tick the delivery-method box above to enable.</span>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
